import { useEffect, useState } from "react";

export type ConnectionType = "wifi" | "cellular" | "unknown";

/** Narrow shape of the Network Information API this hook actually reads -
 * not in lib.dom.d.ts, so declared locally rather than pulling in a whole
 * third-party types package for one optional, non-standard API. */
interface NetworkInformation extends EventTarget {
  type?: string;
  effectiveType?: string;
}

/** Plain (non-hook) read, for callers outside React - e.g. offlineQueue.ts's
 * initOfflineSync(), a module-level function that can't call hooks. */
export function readConnectionType(): ConnectionType {
  const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;
  if (!connection) return "unknown";
  // `type` is the direct answer ("wifi"/"cellular"/"ethernet"/...) but has
  // real, honest gaps: unsupported in Safari/iOS entirely (no `navigator.
  // connection` at all - the `!connection` branch above), and often reports
  // "unknown" even on browsers that do implement the API (privacy-motivated
  // restrictions). There's no reliable way to distinguish "definitely wifi"
  // from "definitely cellular" beyond what the browser is willing to say.
  if (connection.type === "wifi") return "wifi";
  if (connection.type === "cellular") return "cellular";
  return "unknown";
}

/** Wifi vs. mobile data, where the browser is willing to say - many browsers
 * (notably Safari/iOS) don't implement the Network Information API at all,
 * so "unknown" is an expected, common outcome, not an edge case to hide. */
export function useConnectionType(): ConnectionType {
  const [type, setType] = useState<ConnectionType>(readConnectionType);

  useEffect(() => {
    const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;
    if (!connection) return;
    const onChange = () => setType(readConnectionType());
    connection.addEventListener("change", onChange);
    return () => connection.removeEventListener("change", onChange);
  }, []);

  return type;
}
